import PropTypes from "prop-types";
import React, { Component } from "react";
import { reduxForm } from "redux-form";

import Avatar from "components/Avatar";
import AvatarInput from "components/AvatarInput";
import ErrorDisplay from "components/ErrorDisplay";
import Input from "components/Input/Input";
import InputGroup from "components/InputGroup";
import LinkButton from "components/LinkButton";
import LinkButtonGroup from "components/LinkButtonGroup";
import Modal from "components/Modal";
import ModalContent from "components/ModalContent";
import PageBlockAction from "components/PageBlockAction";
import CustomSelect from "components/CustomSelect";
import TextHeading from "components/TextHeading";
import MFAInput from "components/MFAInput";

import genericModalStyles from "helpers/genericModalStyles";

import { modalStatus, modalType, submitSignup } from "./actions";

import "./_style.scss";
import SignupSuccess from "./SignupSuccess";
import TeamNotFoundModal from "./TeamNotFoundModal";
import DefaultModal from "./DefaultModal";
import validate from "./utils/validate";

const fields = [
  "firstName",
  "lastName",
  "email",
  "password",
  "mfaData",
  "image",
  "team",
  "team_name",
  "team_target",
  "team_logo",
];

class SignupForm extends Component {
  static propTypes = {
    campaignId: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
    campaignTitle: PropTypes.string.isRequired,
    campaignUrl: PropTypes.string.isRequired,
    campaignOwner: PropTypes.string.isRequired,
    canCreateTeams: PropTypes.bool.isRequired,
    errorFor: PropTypes.string,
    errorMessage: PropTypes.string,
    fields: PropTypes.object.isRequired,
    fundraiser: PropTypes.object,
    handleSubmit: PropTypes.func.isRequired,
    initialValues: PropTypes.object,
    isError: PropTypes.bool.isRequired,
    isModalOpen: PropTypes.bool.isRequired,
    isSendingLink: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    modalType: PropTypes.string,
    sendLinkSuccess: PropTypes.bool,
    shouldShowThankYou: PropTypes.bool,
    submitSuccess: PropTypes.bool.isRequired,
    teams: PropTypes.arrayOf(PropTypes.object),
    user: PropTypes.object,
  };

  componentDidMount() {
    if (this.props.user && this.props.user.isEditor) {
      this.props.setModalStatus(true);
      this.props.setModalType("editor-access");
    }
  }

  onAvatarInputChange = (imgSrc) => {
    // Update redux-form state to have correct value of avatar
    this.props.fields.image.onChange(imgSrc);
  };

  onLogoInputChange = (imgSrc) => {
    // Update redux-form state to have correct value of avatar
    this.props.fields.team_logo.onChange(imgSrc);
  };

  onSubmit = (data) => {
    this.props.submitAction(data);
  };

  onSubmitEmailLink = (data) => {
    const formData = data;
    formData.action = "email-link";
    this.props.submitAction(formData);
  };

  closeModal = () => {
    this.props.setModalStatus(false);
  };

  selectTeams = (teams) => {
    let teams_array = [];
    if (teams !== false) {
      teams_array = teams.map((item) => ({
        value: item.id,
        name: item.name,
        text: item.name,
      }));
    }

    if (this.props.canCreateTeams === true) {
      teams_array.push({
        value: "-",
        name: "",
        text: "------",
      });
      teams_array.push({
        value: "new",
        name: "Create a new team",
        text: "Create a new team",
      });
    }

    return teams_array;
  };

  renderModal = () => {
    const {
      errorFor, errorMessage, handleSubmit, isError,
    } = this.props;

    switch (this.props.modalType) {
      case "editor-access": {
        const teamUrl = `/edit/project/${this.props.campaignId}/people-fundraisers`;
        return (
          <Modal
            contentLabel="Preview only for user with Campaign Editor access"
            customStyle={genericModalStyles}
            isOpen={this.props.isModalOpen}
            onRequestClose={this.closeModal}
            shouldCloseOnOverlayClick
          >
            <ModalContent
              action={(
                <LinkButtonGroup>
                  <LinkButton look="secondary-outline" href={teamUrl}>
                    Manage fundraisers
                  </LinkButton>
                  <LinkButton look="secondary-outline" onClick={this.closeModal}>
                    Okay, got it
                  </LinkButton>
                </LinkButtonGroup>
              )}
              body={(
                <p>
                  As you're an editor for this campaign, we're showing you a preview of the
                  fundraiser Sign Up page. You won't be able to submit this form while logged in as
                  a user with Campaign Editor access
                  <br />
                  <br />
                  To add a new fundraiser, head to the
                  {" "}
                  <a href={teamUrl}>Fundraisers</a>
                  {" "}
                  section of
                  the campaign editor.
                </p>
              )}
              heading="You're a campaign editor"
            />
          </Modal>
        );
      }
      case "existing-user": {
        return (
          <Modal
            contentLabel="You have an existing account"
            customStyle={genericModalStyles}
            isOpen={this.props.isModalOpen}
            onRequestClose={this.closeModal}
            shouldCloseOnOverlayClick
          >
            <ModalContent
              action={(
                <LinkButtonGroup>
                  <LinkButton
                    isLoading={this.props.isSendingLink}
                    loadingMessage="Sending link... "
                    look="secondary-outline"
                    onClick={handleSubmit(this.onSubmitEmailLink)}
                  >
                    Request an email link
                  </LinkButton>
                </LinkButtonGroup>
              )}
              body={(
                <div>
                  {isError === true && errorFor === "send-link" && (
                    <div className="signup-form__error">{errorMessage}</div>
                  )}
                  <p>
                    It looks like you’re already a part of the Chuffed.org community.
                    <br />
                    <br />
                    <strong>To create your fundraiser page, you can:</strong>
                  </p>
                </div>
              )}
              heading="You've got an existing account"
            />
          </Modal>
        );
      }
      case "existing-user-bad-pw": {
        return (
          <Modal
            contentLabel="You entered an incorrect password for this email address"
            customStyle={genericModalStyles}
            isOpen={this.props.isModalOpen}
            onRequestClose={this.closeModal}
            shouldCloseOnOverlayClick
          >
            <ModalContent
              action={(
                <LinkButtonGroup>
                  <LinkButton look="secondary-outline" onClick={this.closeModal}>
                    Try again
                  </LinkButton>
                  <LinkButton
                    isLoading={this.props.isSendingLink}
                    loadingMessage="Sending link... "
                    look="secondary-outline"
                    onClick={handleSubmit(this.onSubmitEmailLink)}
                  >
                    Request an email link
                  </LinkButton>
                </LinkButtonGroup>
              )}
              body={(
                <div>
                  {isError === true && errorFor === "send-link" && (
                    <div className="signup-form__error">{errorMessage}</div>
                  )}
                  <p>
                    It looks like you’re already a part of the Chuffed.org community.
                    <br />
                    <br />
                    But the password you entered doesn’t match the login details for this email
                    address
                    <br />
                    <br />
                    <strong>To create your fundraiser page, you can:</strong>
                  </p>
                </div>
              )}
              heading="You've got an existing account"
            />
          </Modal>
        );
      }
      case "team-not-found": {
        return <TeamNotFoundModal closeModal={this.closeModal} />;
      }
      default:
        return <DefaultModal closeModal={this.closeModal} message={errorMessage} />;
    }
  };

  render() {
    const {
      campaignTitle,
      campaignUrl,
      campaignOwner,
      canCreateTeams,
      errorFor,
      errorMessage,
      fields: {
        firstName,
        lastName,
        email,
        password,
        mfaData,
        image,
        team,
        team_name,
        team_target,
        team_logo,
      },
      fundraiser,
      handleSubmit,
      isError,
      isSubmitting,
      sendLinkSuccess,
      shouldShowThankYou,
      submitSuccess,
      teams,
      user,
    } = this.props;

    const isCreatingTeam = team.value === "new";

    if (sendLinkSuccess === true) {
      return <SignupSuccess />;
    }

    if (
      submitSuccess === true
      || shouldShowThankYou === true
      || (fundraiser && fundraiser.isExisting === true)
    ) {
      return (
        <div>
          <div className="signup-form__success">
            <div className="signup-form__message">
              <div className="signup-form__message-media">
                <Avatar altText="Profile image of fundraiser" image={fundraiser.userImage} />
              </div>
              <div className="signup-form__message-content">
                <TextHeading level={3} marginBottom="half" size={4}>
                  {fundraiser.isExisting === true
                    ? "You've got an existing fundraiser page"
                    : "Congratulations!"}
                </TextHeading>
                {fundraiser.isExisting === true ? (
                  <p>It looks like you've already got a fundraiser page for this campaign.</p>
                ) : (
                  <p>You're all signed up to be a fundraiser for this campaign.</p>
                )}
                <p>
                  <strong>Your fundraiser page:</strong>
                </p>
                <TextHeading light level={0} marginBottom="none" size={6}>
                  {`chuffed.org/fundraiser/${this.props.fundraiser.slug}`}
                </TextHeading>
                <PageBlockAction
                  action={(
                    <LinkButtonGroup>
                      <LinkButton
                        look="secondary-outline"
                        href={`/fundraiser/${fundraiser.slug}`}
                        target="_blank"
                      >
                        Visit your page
                      </LinkButton>
                      <LinkButton
                        look="secondary-outline"
                        href={`/edit/fundraiser/${fundraiser.id}`}
                        target="_blank"
                      >
                        Edit your fundraiser page
                      </LinkButton>
                    </LinkButtonGroup>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    const mfaRequired = errorMessage?.includes("Multifactor Authentication");

    const normaliseMfaCode = (val) => {
      if (!val) {
        return val;
      }
      const preparedValue = val.target?.value ?? val;
      mfaData.onChange({ code: preparedValue, type: "authenticator" });
    };

    return (
      <div>
        <h2 className="text-heading --align-center --size-3 --margin-bottom-half">
          Sign up as a fundraiser
        </h2>
        <p className="text-body --lede --align-center --margin-bottom-double">
          Create your fundraiser page for
          {" "}
          <a href={campaignUrl}>{campaignTitle}</a>
          {" "}
          by
          {" "}
          {campaignOwner}
          .
        </p>

        <form
          acceptCharset="UTF-8"
          action="/fundraiser/signup"
          className="signup-form__form"
          method="post"
          onSubmit={handleSubmit(this.onSubmit)}
        >
          <InputGroup label="First name*" look="sentence-case" spacing="small" v2>
            <ErrorDisplay alwaysShowErrors={firstName.touched && firstName.error} field={firstName}>
              <Input {...firstName} placeholder="e.g., Jenny" />
            </ErrorDisplay>
          </InputGroup>
          <InputGroup label="Last name*" look="sentence-case" spacing="small" v2>
            <ErrorDisplay alwaysShowErrors={lastName.touched && lastName.error} field={lastName}>
              <Input {...lastName} placeholder="e.g., Smith" />
            </ErrorDisplay>
          </InputGroup>
          {(!user || user.isEditor === true) && (
            <InputGroup label="Your email*" look="sentence-case" spacing="small" v2>
              <ErrorDisplay alwaysShowErrors={email.touched && email.error} field={email}>
                <Input {...email} type="email" placeholder="email@example.com" />
              </ErrorDisplay>
            </InputGroup>
          )}
          {(!user || user.isEditor === true) && (
            <>
              <InputGroup label="Password*" look="sentence-case" spacing="small" v2>
                <ErrorDisplay alwaysShowErrors={password.touched && password.error} field={password}>
                  <Input {...password} type="password" placeholder="********" />
                </ErrorDisplay>
              </InputGroup>
              {mfaRequired && (
                <MFAInput
                  {...mfaData}
                  onChange={normaliseMfaCode}
                  allowRecovery={false}
                  variant="inline"
                />
              )}
            </>
          )}
          <InputGroup label="Profile image*" look="sentence-case" spacing="small" v2>
            <ErrorDisplay alwaysShowErrors={image.touched && image.error} field={image}>
              <AvatarInput
                {...image}
                cropRatio={1}
                imageWidth={200}
                imageHeight={200}
                onChange={this.onAvatarInputChange}
                placeholder="Profile picture of you or logo"
                src={image.value}
              />
            </ErrorDisplay>
          </InputGroup>
          {(teams.length > 0 || canCreateTeams === true) && (
            <div>
              <InputGroup label="Team" look="sentence-case" spacing="small" v2>
                <ErrorDisplay alwaysShowErrors={team.touched && team.error} field={team}>
                  <CustomSelect
                    name="team_id"
                    onChange={this.teamSelected}
                    options={this.selectTeams(teams)}
                    defaultOption="No team"
                    {...team}
                  />
                </ErrorDisplay>
              </InputGroup>
              {isCreatingTeam === true && (
                <div>
                  <InputGroup label="Team name*" look="sentence-case" spacing="small" v2>
                    <ErrorDisplay
                      alwaysShowErrors={team_name.touched && team_name.error}
                      field={team_name}
                    >
                      <Input {...team_name} placeholder="e.g., Blue Team" />
                    </ErrorDisplay>
                  </InputGroup>
                  <InputGroup label="Team target*" look="sentence-case" spacing="small" v2>
                    <ErrorDisplay
                      alwaysShowErrors={team_target.touched && team_target.error}
                      field={team_target}
                    >
                      <Input {...team_target} placeholder="e.g., 12000" type="number" />
                    </ErrorDisplay>
                  </InputGroup>
                  <InputGroup label="Team Logo*" look="sentence-case" spacing="small" v2>
                    <ErrorDisplay
                      alwaysShowErrors={team_logo.touched && team_logo.error}
                      field={team_logo}
                    >
                      <AvatarInput
                        {...team_logo}
                        cropRatio={1}
                        imageWidth={200}
                        imageHeight={200}
                        onChange={this.onLogoInputChange}
                        placeholder="Team logo image"
                        src={team_logo.value}
                      />
                    </ErrorDisplay>
                  </InputGroup>
                </div>
              )}
            </div>
          )}
          <InputGroup label="" spacing="none" v2>
            {isError === true && errorFor === "submit" && (
              <div className="signup-form__error">{errorMessage}</div>
            )}
            <LinkButton
              onClick={handleSubmit(this.onSubmit)}
              type="submit"
              look="primary"
              isBold
              isDisabled={user && user.isEditor === true}
              size="large"
              isLoading={isSubmitting}
              loadingMessage="Submitting... "
            >
              Sign up
            </LinkButton>
          </InputGroup>
          {this.props.isModalOpen === true ? this.renderModal() : null}
        </form>
      </div>
    );
  }
}

const SignupReduxForm = reduxForm(
  {
    form: "signupForm",
    fields,
    validate,
  },
  (state) => ({
    campaignId: state.fundraiserSignup.campaignId || null,
    campaignTitle: state.fundraiserSignup.campaignTitle || null,
    campaignUrl: state.fundraiserSignup.campaignUrl || null,
    campaignOwner: state.fundraiserSignup.campaignOwner || null,
    canCreateTeams: state.fundraiserSignup.canCreateTeams || false,
    errorFor: state.fundraiserSignup.errorFor || "",
    errorMessage: state.fundraiserSignup.errorMessage || "",
    fundraiser: state.fundraiserSignup.fundraiser || null,
    initialValues: {
      firstName: (state.fundraiserSignup.user && state.fundraiserSignup.user.firstName) || "",
      lastName: (state.fundraiserSignup.user && state.fundraiserSignup.user.lastName) || "",
      email: (state.fundraiserSignup.user && state.fundraiserSignup.user.email) || "",
      image: (state.fundraiserSignup.user && state.fundraiserSignup.user.image) || "",
      team: (state.fundraiserSignup.user && state.fundraiserSignup.user.team) || "",
      team_name: (state.fundraiserSignup.user && state.fundraiserSignup.user.team_name) || "",
      team_target: (state.fundraiserSignup.user && state.fundraiserSignup.user.team_target) || "",
      team_logo: (state.fundraiserSignup.user && state.fundraiserSignup.user.team_logo) || "",
    },
    isError: state.fundraiserSignup.isError || false,
    isModalOpen: state.fundraiserSignup.isModalOpen,
    isSendingLink: state.fundraiserSignup.isSendingLink || false,
    isSubmitting: state.fundraiserSignup.isSubmitting || false,
    modalType: state.fundraiserSignup.modalType,
    sendLinkSuccess: state.fundraiserSignup.sendLinkSuccess || false,
    shouldShowThankYou: state.fundraiserSignup.shouldShowThankYou || false,
    submitSuccess: state.fundraiserSignup.submitSuccess || false,
    teams: state.fundraiserSignup.teams || false,
    user: state.fundraiserSignup.user || null,
  }),
  (dispatch) => ({
    submitAction: (data) => dispatch(submitSignup(data)),
    setModalStatus: (data) => dispatch(modalStatus(data)),
    setModalType: (data) => dispatch(modalType(data)),
  }),
)(SignupForm);

export default SignupReduxForm;
